import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useRole } from "../../components/AppShell";
import { PlusIcon } from "../../components/icons";
import { Empty, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";
import { useContacts, useMe, useProducts, useTeam } from "../../lib/queries";

interface Appointment {
  id: string;
  title: string;
  productId: string | null;
  userId: string;
  startsAt: string;
  endsAt: string;
  status: "confirmed" | "cancelled" | "done" | "no_show";
  notes: string | null;
  createdBy: string;
  contact: { id: string; name: string | null; waId: string };
}
interface Slot {
  startsAt: string;
  endsAt: string;
  userIds: string[];
}
interface AgendaSettings {
  generalLabel: string;
  generalMinutes: number;
  stepMinutes: number;
  minNoticeMinutes: number;
  daysAhead: number;
  members: { userId: string; active: boolean; general: boolean; serviceIds: string[] }[];
}

const TZ = "America/Costa_Rica";
const dayFmt = new Intl.DateTimeFormat("es-CR", { weekday: "long", day: "numeric", month: "long", timeZone: TZ });
const timeFmt = new Intl.DateTimeFormat("es-CR", { hour: "numeric", minute: "2-digit", timeZone: TZ });
const STATUS = {
  confirmed: { label: "Confirmada", pill: "pill-human" },
  done: { label: "Atendida", pill: "pill-bot" },
  no_show: { label: "No vino", pill: "pill-warn" },
  cancelled: { label: "Cancelada", pill: "pill-closed" },
} as const;

/** "2026-11-09" for a date, in Costa Rica. */
const crDay = (d: Date) => new Date(d.getTime() - 6 * 3600_000).toISOString().slice(0, 10);
const crDate = (day: string) => new Date(`${day}T12:00:00-06:00`);
const addDays = (day: string, n: number) => crDay(new Date(crDate(day).getTime() + n * 86_400_000));
/** The Monday of that day's week. */
function monday(day: string) {
  const dow = crDate(day).getUTCDay();
  return addDays(day, dow === 0 ? -6 : 1 - dow);
}
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The agenda (f6-13): the week's appointments, with whom and for what. Everyone books,
 * moves and closes them; owners and admins set who attends what. Lumi books here too.
 */
export function AgendaPage() {
  const role = useRole();
  const manage = role === "owner" || role === "admin";
  const [week, setWeek] = useState(() => monday(crDay(new Date())));
  const [person, setPerson] = useState("");
  const [creating, setCreating] = useState(false);
  const [moving, setMoving] = useState<Appointment | null>(null);
  const [setup, setSetup] = useState(false);
  const team = useTeam();
  const names = new Map((team.data?.items ?? []).map((m) => [m.id, m.name]));
  const from = `${week}T00:00:00-06:00`;
  const to = `${addDays(week, 6)}T23:59:59-06:00`;
  const list = useQuery({
    queryKey: ["agenda", "appointments", week, person],
    queryFn: () =>
      api.get<{ items: Appointment[] }>(
        `/agenda/appointments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${person ? `&userId=${person}` : ""}`,
      ),
  });
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const items = list.data?.items ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Agenda</h1>
        <div className="row">
          {manage && (
            <button className="btn" type="button" onClick={() => setSetup(true)}>
              Ajustes de agenda
            </button>
          )}
          <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>
            <PlusIcon />
            Nueva cita
          </button>
        </div>
      </div>
      <div className="agenda-bar">
        <div className="row">
          <button
            className="btn btn-sm"
            type="button"
            aria-label="Semana anterior"
            onClick={() => setWeek(addDays(week, -7))}
          >
            ‹
          </button>
          <button className="btn btn-sm" type="button" onClick={() => setWeek(monday(crDay(new Date())))}>
            Esta semana
          </button>
          <button
            className="btn btn-sm"
            type="button"
            aria-label="Semana siguiente"
            onClick={() => setWeek(addDays(week, 7))}
          >
            ›
          </button>
          <strong className="agenda-range">
            {capital(dayFmt.format(crDate(week)))} – {dayFmt.format(crDate(addDays(week, 6)))}
          </strong>
        </div>
        {manage && (
          <select className="select" aria-label="Persona" value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">Todo el equipo</option>
            {(team.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {list.isPending ? (
        <div className="card">
          <ListSkeleton />
        </div>
      ) : !items.length ? (
        <div className="card">
          <Empty title="No hay citas esta semana.">
            <span>Las que agenden el equipo o Lumi aparecen aquí.</span>
          </Empty>
        </div>
      ) : (
        <div className="agenda-days">
          {days.map((day) => {
            const today = items.filter((a) => crDay(new Date(a.startsAt)) === day);
            if (!today.length) return null;
            return (
              <section key={day} className="card">
                <div className="card-body">
                  <h3 className={`agenda-day${day === crDay(new Date()) ? " is-today" : ""}`}>
                    {capital(dayFmt.format(crDate(day)))}
                  </h3>
                  <ul className="agenda-list">
                    {today.map((a) => (
                      <AppointmentRow
                        key={a.id}
                        a={a}
                        person={names.get(a.userId) ?? "Alguien que ya no está"}
                        onMove={() => setMoving(a)}
                      />
                    ))}
                  </ul>
                </div>
              </section>
            );
          })}
        </div>
      )}
      {creating && <BookDialog onClose={() => setCreating(false)} names={names} />}
      {moving && <BookDialog onClose={() => setMoving(null)} names={names} moving={moving} />}
      {setup && <SettingsDialog onClose={() => setSetup(false)} />}
    </div>
  );
}

function AppointmentRow({ a, person, onMove }: { a: Appointment; person: string; onMove: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const set = useMutation({
    mutationFn: (status: Appointment["status"]) => api.patch(`/agenda/appointments/${a.id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agenda"] }),
    onError: () => toast("No se pudo cambiar. Intenta de nuevo."),
  });
  const s = STATUS[a.status];
  return (
    <li className={a.status === "confirmed" ? undefined : "is-closed"}>
      <span className="agenda-time">
        {timeFmt.format(new Date(a.startsAt))} – {timeFmt.format(new Date(a.endsAt))}
      </span>
      <span className="agenda-main">
        <strong>{a.title}</strong>
        <span>
          <Link to="/contactos/$contactId" params={{ contactId: a.contact.id }}>
            {a.contact.name ?? a.contact.waId}
          </Link>{" "}
          · con {person}
          {a.createdBy === "lumi" ? " · la agendó Lumi" : ""}
        </span>
        {a.notes && <span className="faint">{a.notes}</span>}
      </span>
      <span className="agenda-actions">
        <span className={`pill ${s.pill}`}>{s.label}</span>
        {a.status === "confirmed" && (
          <>
            <button className="btn btn-sm" type="button" onClick={onMove}>
              Mover
            </button>
            <button className="btn btn-sm" type="button" disabled={set.isPending} onClick={() => set.mutate("done")}>
              Atendida
            </button>
            <button
              className="btn btn-sm btn-ghost"
              type="button"
              disabled={set.isPending}
              onClick={() => set.mutate("no_show")}
            >
              No vino
            </button>
            <button
              className="btn btn-sm btn-ghost"
              type="button"
              disabled={set.isPending}
              onClick={async () => {
                if (
                  await confirm({
                    title: "¿Cancelar la cita?",
                    message: "La hora queda libre otra vez.",
                    confirmLabel: "Cancelar cita",
                  })
                )
                  set.mutate("cancelled");
              }}
            >
              Cancelar
            </button>
          </>
        )}
      </span>
    </li>
  );
}

const REASON: Record<string, string> = {
  outside_hours: "Esa hora está fuera del horario de la empresa.",
  too_soon: "Es muy pronto para agendar a esa hora.",
};

/** A new appointment, or a new time for one: pick the day, then one of its free times. */
function BookDialog({
  onClose,
  names,
  moving,
}: {
  onClose: () => void;
  names: Map<string, string>;
  moving?: Appointment;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState("");
  const contacts = useContacts(useDeferredValue(q.trim()));
  const services = useProducts("", "", { kind: "service" });
  const [contactId, setContactId] = useState("");
  const [serviceId, setServiceId] = useState(moving?.productId ?? "");
  const [day, setDay] = useState(moving ? crDay(new Date(moving.startsAt)) : crDay(new Date()));
  const [slot, setSlot] = useState<Slot | null>(null);
  const [userId, setUserId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const slots = useQuery({
    queryKey: ["agenda", "slots", serviceId, day],
    queryFn: () =>
      api.get<{ title: string; minutes: number; slots: Slot[] }>(
        `/agenda/slots?from=${day}&days=1${serviceId ? `&serviceId=${serviceId}` : ""}`,
      ),
  });
  const save = useMutation({
    mutationFn: () =>
      moving
        ? api.patch(`/agenda/appointments/${moving.id}`, { startsAt: slot?.startsAt, ...(userId ? { userId } : {}) })
        : api.post("/agenda/appointments", {
            contactId,
            productId: serviceId || null,
            startsAt: slot?.startsAt,
            ...(userId ? { userId } : {}),
            notes: notes || null,
          }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
      toast(moving ? "Cita movida" : "Cita agendada");
      dialog.current?.close();
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? String(err.details?.reason ?? "") : "";
      setError(
        REASON[reason] ??
          (err instanceof ApiError && err.status === 409
            ? "Esa hora ya se ocupó. Elige otra."
            : "No se pudo guardar. Intenta de nuevo."),
      );
      qc.invalidateQueries({ queryKey: ["agenda", "slots"] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!slot || (!moving && !contactId)) return;
    setError(null);
    save.mutate();
  };
  const free = slots.data?.slots ?? [];
  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="book-title" className="agenda-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="book-title">
          {moving ? `Mover la cita de ${moving.contact.name ?? moving.contact.waId}` : "Nueva cita"}
        </h3>
        {!moving && (
          <>
            <label className="field">
              <span>Cliente</span>
              <input
                className="input"
                type="search"
                placeholder="Busca por nombre o teléfono, ej.: Ana"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setContactId("");
                }}
              />
            </label>
            {!contactId && q.trim() && (
              <ul className="agenda-picks">
                {(contacts.data?.items ?? []).slice(0, 6).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setContactId(c.id);
                        setQ(c.name ?? c.waId);
                      }}
                    >
                      {c.name ?? "Sin nombre"} · {c.waId}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className="field">
              <span>Qué se agenda</span>
              <select
                className="select"
                value={serviceId}
                onChange={(e) => {
                  setServiceId(e.target.value);
                  setSlot(null);
                }}
              >
                <option value="">{slots.data && !serviceId ? slots.data.title : "Cita general"}</option>
                {(services.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.durationMinutes ? ` · ${s.durationMinutes} min` : ""}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        <label className="field">
          <span>Día</span>
          <input
            className="input"
            type="date"
            placeholder="Ej.: 2026-11-10"
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              setSlot(null);
            }}
          />
        </label>
        <div className="field">
          <span>Hora libre</span>
          {slots.isPending ? (
            <p className="muted">Buscando horas…</p>
          ) : free.length ? (
            <div className="agenda-slots">
              {free.map((s) => (
                <button
                  key={s.startsAt}
                  type="button"
                  className={`btn btn-sm${slot?.startsAt === s.startsAt ? " btn-primary" : ""}`}
                  onClick={() => {
                    setSlot(s);
                    setUserId("");
                  }}
                >
                  {timeFmt.format(new Date(s.startsAt))}
                </button>
              ))}
            </div>
          ) : (
            <p className="muted">
              No hay horas libres ese día. Revisa el horario de la empresa y, en Ajustes de agenda, quién atiende esto.
            </p>
          )}
        </div>
        {slot && slot.userIds.length > 1 && (
          <label className="field">
            <span>Con quién</span>
            <select className="select" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Quien tenga menos citas ese día</option>
              {slot.userIds.map((u) => (
                <option key={u} value={u}>
                  {names.get(u) ?? u}
                </option>
              ))}
            </select>
          </label>
        )}
        {!moving && (
          <label className="field">
            <span>Notas (opcional)</span>
            <input
              className="input"
              placeholder="Ej.: Lleva la factura de compra"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        )}
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cerrar
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!slot || (!moving && !contactId) || save.isPending}
          >
            {save.isPending ? "Guardando…" : moving ? "Mover" : "Agendar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Who attends what, and how times are offered. Owners and admins. */
function SettingsDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const team = useTeam();
  const services = useProducts("", "", { kind: "service" });
  const current = useQuery({
    queryKey: ["agenda", "settings"],
    queryFn: () => api.get<AgendaSettings>("/agenda/settings"),
  });
  const [form, setForm] = useState<AgendaSettings | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  useEffect(() => {
    if (current.data && !form) setForm(current.data);
  }, [current.data, form]);
  const members = useMemo(
    () =>
      (team.data?.items ?? []).map(
        (m) =>
          form?.members.find((x) => x.userId === m.id) ?? {
            userId: m.id,
            active: false,
            general: true,
            serviceIds: [],
          },
      ),
    [team.data, form],
  );
  const save = useMutation({
    mutationFn: (input: AgendaSettings) => api.put("/agenda/settings", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agenda"] });
      toast("Ajustes de agenda guardados");
      dialog.current?.close();
    },
    onError: () => toast("No se pudo guardar. Revisa los números."),
  });
  const setMember = (userId: string, patch: Partial<AgendaSettings["members"][number]>) =>
    form &&
    setForm({
      ...form,
      members: members.map((m) => (m.userId === userId ? { ...m, ...patch } : m)),
    });
  const me = useMe();

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="agset-title" className="agenda-dialog">
      {!form ? (
        <p className="muted">Cargando…</p>
      ) : (
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ ...form, members });
          }}
        >
          <h3 id="agset-title">Ajustes de agenda</h3>
          <p className="muted">
            Las horas libres salen del horario de la empresa (Empresa › Horario) y de las citas de cada persona.
          </p>
          <div className="agenda-fields">
            <label className="field">
              <span>Nombre de la cita general</span>
              <input
                className="input"
                required
                placeholder="Ej.: Visita técnica"
                value={form.generalLabel}
                onChange={(e) => setForm({ ...form, generalLabel: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Dura (minutos)</span>
              <input
                className="input"
                type="number"
                min={5}
                max={480}
                placeholder="Ej.: 30"
                value={form.generalMinutes}
                onChange={(e) => setForm({ ...form, generalMinutes: Number(e.target.value) })}
              />
            </label>
            <label className="field">
              <span>Ofrecer horas cada</span>
              <select
                className="select"
                value={form.stepMinutes}
                onChange={(e) => setForm({ ...form, stepMinutes: Number(e.target.value) })}
              >
                {[10, 15, 20, 30, 60].map((n) => (
                  <option key={n} value={n}>
                    {n} minutos
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Anticipación mínima (horas)</span>
              <input
                className="input"
                type="number"
                min={0}
                max={168}
                placeholder="Ej.: 2"
                value={form.minNoticeMinutes / 60}
                onChange={(e) => setForm({ ...form, minNoticeMinutes: Math.round(Number(e.target.value) * 60) })}
              />
            </label>
            <label className="field">
              <span>Hasta cuántos días adelante</span>
              <input
                className="input"
                type="number"
                min={1}
                max={180}
                placeholder="Ej.: 30"
                value={form.daysAhead}
                onChange={(e) => setForm({ ...form, daysAhead: Number(e.target.value) })}
              />
            </label>
          </div>
          <h4>Quién atiende</h4>
          <ul className="agenda-members">
            {(team.data?.items ?? []).map((t) => {
              const m = members.find((x) => x.userId === t.id);
              if (!m) return null;
              return (
                <li key={t.id}>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={m.active}
                      onChange={(e) => setMember(t.id, { active: e.target.checked })}
                    />
                    <strong>
                      {t.name}
                      {t.id === me.data?.user.id ? " (tú)" : ""}
                    </strong>
                  </label>
                  {m.active && (
                    <div className="agenda-member-services">
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={m.general}
                          onChange={(e) => setMember(t.id, { general: e.target.checked })}
                        />
                        {form.generalLabel}
                      </label>
                      {(services.data?.items ?? []).map((s) => (
                        <label key={s.id} className="check">
                          <input
                            type="checkbox"
                            checked={m.serviceIds.includes(s.id)}
                            onChange={(e) =>
                              setMember(t.id, {
                                serviceIds: e.target.checked
                                  ? [...m.serviceIds, s.id]
                                  : m.serviceIds.filter((x) => x !== s.id),
                              })
                            }
                          />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="row">
            <button className="btn" type="button" onClick={() => dialog.current?.close()}>
              Cancelar
            </button>
            <button className="btn btn-primary" type="submit" disabled={save.isPending}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
